import { FC, useState } from "react";
import classes from './tutorDownloadReceipts.module.scss'
import { useGlobalLoadingActions } from "../../../entities/globalLoading";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { tutorService } from "../../../entities/tutor";
import { ITutorReceiptItem } from "../../../entities/tutor/model/types";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { FEATURES, hasFeature, useMyActions } from "../../../entities/my";
import { Calendar } from "../../../shared/ui/calendar";
import { getDateUTC } from "../../../shared/lib/helpers/getDateUTC";
import { LoaderSpinner } from "../../../shared/ui/spinner";
import { ReceiptIcon } from "../../../shared/lib/assets/ReceiptIcon";
import { formatUtcToMsk } from "../../../shared/lib/helpers/formatUtcToMsk";
import { useAppSelector } from "../../../app/store/store";
import { TrashIcon } from "../../../shared/ui/icon";

interface IProps {
    tutorId: number;
}

export const TutorDownloadReceipts: FC<IProps> = ({tutorId}) => {

    const {setIsLoading} = useGlobalLoadingActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const {my} = useAppSelector(state => state.myReducer)
    const canCancelPayout = hasFeature(my, FEATURES.TUTORS.PAYOUTS_CANCEL, tutorId)
    const canDeleteReceipt = hasFeature(my, FEATURES.TUTORS.RECEIPTS_DELETE, tutorId)
    const [receipts, setReceipts] = useState<ITutorReceiptItem[] | null>(null)
    const [isLoading, setLocalLoading] = useState<boolean>(false)
    const [startDate, setStartDate] = useState<Date | null>(null)
    const [endDate, setEndDate] = useState<Date | null>(null)

    const download = (blob: Blob, fileName: string) => {
        const a = document.createElement('a')
        const url = URL.createObjectURL(blob);
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }

    const getReceipts = async (from: string, to: string) => {
        try{
            setLocalLoading(true)
            const receiptsRes = await tutorService.getReceiptList(tutorId, from, to)
            setReceipts(receiptsRes)
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при получении чеков исполнителя', type: 'error'})
            }
        }
        finally{
            setLocalLoading(false)
        }
    }

    const downloadReceipt = (receipt: ITutorReceiptItem) => {
        return async () => {
            if(!receipt.hasReceipt) return

            try{
                setIsLoading(true)
                const {blob, fileName} = await tutorService.downloadReceipt(receipt.id)
                download(blob, fileName)
            }
            catch(e){
                console.log(e)
                setGlobalMessage({message: 'Ошибка при скачивании чека исполнителя', type: 'error'})
            }
            finally{
                setIsLoading(false)
            }
        }
    }

    const cancelPayout = (receipt: ITutorReceiptItem) => {
        return async () => {
            if(receipt.hasReceipt || !window.confirm('Отменить выплату? Начисления вернутся в баланс сотрудника.')){
                return
            }

            try{
                setIsLoading(true)
                await tutorService.cancelPayout(tutorId, receipt.id)
                setGlobalMessage({message: 'Выплата отменена', type: 'ok'})
                if(startDate && endDate){
                    await getReceipts(getDateUTC(startDate), getDateUTC(endDate))
                }
            }
            catch(e){
                console.log(e)
                if(e instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else if(e instanceof MyError){
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при отмене выплаты', type: 'error'})
                }
            }
            finally{
                setIsLoading(false)
            }
        }
    }

    const deleteReceipt = (receipt: ITutorReceiptItem) => {
        return async () => {
            if(!receipt.hasReceipt || !window.confirm('Удалить чек? После удаления выплату можно будет отменить, а сотрудник снова увидит выплату без чека.')){
                return
            }

            try{
                setIsLoading(true)
                await tutorService.deleteReceipt(tutorId, receipt.id)
                setGlobalMessage({message: 'Чек удален', type: 'ok'})
                if(startDate && endDate){
                    await getReceipts(getDateUTC(startDate), getDateUTC(endDate))
                }
            }
            catch(e){
                console.log(e)
                if(e instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else if(e instanceof MyError){
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при удалении чека', type: 'error'})
                }
            }
            finally{
                setIsLoading(false)
            }
        }
    }
    
    const setDate = (newStartDate: Date | null, newEndDate: Date | null) => {
        setStartDate(newStartDate)
        setEndDate(newEndDate)
        if(newStartDate && newEndDate){
            getReceipts(getDateUTC(newStartDate), getDateUTC(newEndDate))
        }
    }

    return (
        <section className={classes.container}>
            <section className={classes.sign}>
                Выплаты за промежуток
            </section>
            <Calendar onDateRangeSelect={setDate} /> 
            {
                isLoading
                    ?
                <section className={classes.loader}><LoaderSpinner /></section>
                    :
                receipts
                    &&
                <>
                    <section className={classes.count}>Кол-во чеков: {receipts.length}</section>
                    {
                        receipts.length
                            ?
                        <>
                            <section className={classes.wrapTable}>
                                <table className={classes.table}>
                                    <thead>
                                        <tr className={classes.item}>
                                            <th>Дата</th>
                                            <th>Сумма</th>
                                            <th>Комментарий</th>
                                            <th>Загружен</th>
                                            <th></th>
                                            {canDeleteReceipt && <th></th>}
                                            {canCancelPayout && <th></th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {receipts.map(receipt =>
                                            <tr key={receipt.id} className={classes.row}>
                                                <td>{formatUtcToMsk(receipt.createdAt)} (мск)</td>
                                                <td>{receipt.amount}</td>
                                                <td>{receipt.comment}</td>
                                                <td>{receipt.hasReceipt ? <>&#9989;</> : <>&#10060;</>}</td>
                                                <td>
                                                    <button
                                                        className={classes.receiptButton}
                                                        disabled={!receipt.hasReceipt}
                                                        onClick={downloadReceipt(receipt)}
                                                        title={receipt.hasReceipt ? "Скачать чек" : "Чек не загружен"}
                                                        type="button"
                                                    >
                                                        <ReceiptIcon />
                                                    </button>
                                                </td>
                                                {canDeleteReceipt && (
                                                    <td>
                                                        <button
                                                            className={classes.deleteReceiptButton}
                                                            disabled={!receipt.hasReceipt}
                                                            onClick={deleteReceipt(receipt)}
                                                            title={receipt.hasReceipt ? "Удалить чек" : "Чек не загружен"}
                                                            type="button"
                                                        >
                                                            <TrashIcon size={18} />
                                                        </button>
                                                    </td>
                                                )}
                                                {canCancelPayout && (
                                                    <td>
                                                        <button
                                                            className={classes.cancelButton}
                                                            disabled={receipt.hasReceipt}
                                                            onClick={cancelPayout(receipt)}
                                                            title={receipt.hasReceipt ? "Нельзя отменить выплату с загруженным чеком" : "Отменить выплату"}
                                                            type="button"
                                                        >
                                                            Отменить
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </section>
                            <section className={classes.items}>
                                {receipts.map(receipt =>
                                    <section key={receipt.id} className={classes.card}>
                                        <section className={classes.cardInfo}>
                                            <section>{formatUtcToMsk(receipt.createdAt)} (мск)</section>
                                            <section>{receipt.amount}</section>
                                            <section>{receipt.comment}</section>
                                            <section>{receipt.hasReceipt ? <>&#9989;</> : <>&#10060;</>}</section>
                                        </section>
                                        <section className={classes.cardActions}>
                                            <button
                                                className={classes.receiptButton}
                                                disabled={!receipt.hasReceipt}
                                                onClick={downloadReceipt(receipt)}
                                                title={receipt.hasReceipt ? "Скачать чек" : "Чек не загружен"}
                                                type="button"
                                            >
                                                <ReceiptIcon />
                                            </button>
                                            {canDeleteReceipt && (
                                                <button
                                                    className={classes.deleteReceiptButton}
                                                    disabled={!receipt.hasReceipt}
                                                    onClick={deleteReceipt(receipt)}
                                                    title={receipt.hasReceipt ? "Удалить чек" : "Чек не загружен"}
                                                    type="button"
                                                >
                                                    <TrashIcon size={18} />
                                                </button>
                                            )}
                                            {canCancelPayout && (
                                                <button
                                                    className={classes.cancelButton}
                                                    disabled={receipt.hasReceipt}
                                                    onClick={cancelPayout(receipt)}
                                                    title={receipt.hasReceipt ? "Нельзя отменить выплату с загруженным чеком" : "Отменить выплату"}
                                                    type="button"
                                                >
                                                    Отменить
                                                </button>
                                            )}
                                        </section>
                                    </section>
                                )}
                            </section>
                        </>
                            :
                        <section className={classes.empty}>Чеки не найдены</section>
                    }
                </>
            }
        </section>
    )
}
